import React, { useMemo, useState } from "react";
import { GoDotFill } from "react-icons/go";
import { FaPlus } from "react-icons/fa6";
import LoadingSkeletonImageDynamic from "../../../components/LoadingSkeletonImageDynamic";
import { setViewImage } from "../../../store/Slices/ViewManagerSlice";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks";
import { useFile } from "../../../context/FileProvider";
import { ContactType, UpdateGroupRes } from "../../../types/ResType";
import { updateCurrentConversation } from "../../../store/Slices/CurrentConversationSlice";
import toast from "react-hot-toast";
import useApiPost from "../../../hooks/PostData";
import TextTranslate from "../../../utils/TextTranslate";
import { useConversationMembers } from "../../../store/api/useConversationMembers";
import { updateByChatId } from "../../../store/Slices/ChatListSlice";
import { formatPhoneDisplay } from "../../../utils/formatPhoneDisplay";

export default function GroupHeader() {
  const { setSelectedFile } = useFile();
  const { data: GroupMemberData } = useConversationMembers();
  const OnlineUserList = useAppSelector((state) => state.OnlineUserList);
  const userData = useAppSelector((state) => state.userData);
  const [selectedFileUrl, setSelectedFileUrl] = useState("");
  const { postData } = useApiPost();
  const dispatch = useAppDispatch();
  const currentConversationData = useAppSelector(
    (state) => state.CurrentConversation,
  );
  const ContactList = useAppSelector((state) => state.ContactList);
  const chatList = useAppSelector((state) => state.chatList);
  const isGroupAdmin = !!GroupMemberData?.data?.Records?.find(
    (row) => row.User?.user_id === userData.user_id,
  )?.is_admin;

  const peerFromMembers = useMemo(() => {
    const uid = currentConversationData.user_id;
    return GroupMemberData?.data?.Records?.find(
      (row) => row.user_id == uid || row.User?.user_id == uid,
    )?.User;
  }, [GroupMemberData, currentConversationData.user_id]);

  const peerFromChatList = useMemo(() => {
    const chat = chatList?.Chats?.find(
      (c) => c?.Records?.[0]?.chat_id == currentConversationData.chat_id,
    );
    return chat?.PeerUserData;
  }, [chatList, currentConversationData.chat_id]);

  const displayName =
    currentConversationData.chat_type == "group"
      ? currentConversationData.group_name
      : (ContactList?.data?.find(
          (e: ContactType) => e?.user_id == currentConversationData.user_id,
        )?.name ?? currentConversationData.full_name);

  const phoneLabel = formatPhoneDisplay(
    peerFromMembers?.country_code ||
      currentConversationData.country_code ||
      peerFromChatList?.country_code,
    peerFromMembers?.mobile_num ||
      currentConversationData.mobile_num ||
      peerFromChatList?.mobile_num,
  );

  const isOnline = OnlineUserList.onlineUsers.some(
    (user) => user.user_id == currentConversationData.user_id,
  );

  const onFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!isGroupAdmin) return;
    setSelectedFile(null);
    const files = event.target.files![0];
    if (!files) return;
    setSelectedFile(files);
    setSelectedFileUrl(URL.createObjectURL(files));

    const editGroupFormData = new FormData();
    editGroupFormData.append(
      "chat_id",
      currentConversationData.chat_id.toString(),
    );
    editGroupFormData.append("group_icon", files!);
    editGroupFormData.append("pictureType", "group_icon");
    const updateGroupProfile: UpdateGroupRes = await postData(
      "chat/update-group",
      editGroupFormData,
      "multipart/form-data",
    );

    if (updateGroupProfile.status == true) {
      toast.success("Group Profile Updated", { position: "top-right" });
      dispatch(
        updateCurrentConversation({
          group_icon: updateGroupProfile.data.group_icon,
        }),
      );
      dispatch(
        updateByChatId({
          chat_id: currentConversationData.chat_id,
          group_icon: updateGroupProfile.data.group_icon,
        }),
      );
    }
  };

  const avatarUrl =
    currentConversationData.chat_type == "group"
      ? selectedFileUrl || currentConversationData.group_icon!
      : currentConversationData.profile_pic!;

  return (
    <div className="flex flex-col items-center px-6 pb-5 pt-2 text-center">
      <div className="relative mb-3">
        <button
          type="button"
          className="h-[7.25rem] w-[7.25rem] overflow-hidden rounded-full bg-primary shadow-sm ring-1 ring-black/5 dark:ring-white/10"
          onClick={() => {
            dispatch(
              setViewImage({
                show_image: true,
                image_src: [
                  currentConversationData.chat_type == "group"
                    ? currentConversationData.group_icon!
                    : currentConversationData.profile_pic!,
                ],
              }),
            );
          }}
        >
          <LoadingSkeletonImageDynamic
            radius=""
            className="h-full w-full cursor-pointer object-cover"
            image_height=""
            image_url={avatarUrl}
            image_width=""
          />
        </button>

        {currentConversationData.chat_type !== "group" && isOnline ? (
          <span className="absolute bottom-1 right-1 h-4 w-4 rounded-full border-[3px] border-secondary bg-emerald-500" />
        ) : null}

        {currentConversationData.chat_type == "group" && isGroupAdmin && (
          <>
            <input
              onChange={onFileChange}
              accept="image/*"
              className="hidden"
              type="file"
              id="groupPhotoEdit"
            />
            <label
              htmlFor="groupPhotoEdit"
              className="absolute bottom-0 right-0 z-10 grid h-8 w-8 cursor-pointer place-content-center rounded-full bg-primary shadow ring-1 ring-black/10 dark:ring-white/15"
            >
              <FaPlus className="text-sm text-themeColor" />
            </label>
          </>
        )}
      </div>

      <div className="max-w-full px-2 text-[1.375rem] font-semibold leading-tight tracking-tight text-darkText">
        <span className="line-clamp-2 break-words">{displayName}</span>
      </div>

      <div className="mt-1 text-[13px] font-normal tabular-nums tracking-wide text-lightText">
        {currentConversationData.chat_type == "group" ? (
          <span className="inline-flex items-center gap-1.5">
            <TextTranslate text="Group" />
            <GoDotFill className="h-2 w-2 opacity-50" />
            {GroupMemberData?.data?.Records?.length ?? 0}{" "}
            <TextTranslate text="Members" />
          </span>
        ) : (
          phoneLabel || null
        )}
      </div>
    </div>
  );
}
